import { Menu, MenuSection, MenuItem, DietaryInfo } from '../../types/menu'
import { MenuImage } from './menuImages'
import { needsReview } from '../../utils/menuReview'
import { isKnownCurrencySymbol } from '../../utils/currency'

export interface ClaudeParseResult {
  menu: Menu
  confidence: number
  tokensUsed: { input: number; output: number }
  warnings: string[]
}

export interface APIQuotaError extends Error {
  isQuotaError: boolean
  errorType: 'quota_exceeded' | 'rate_limit' | 'invalid_key' | 'insufficient_credits' | 'service_unavailable'
  userMessage: string
  retryAfter?: number
}

const UNAVAILABLE_MESSAGE = 'Sorry! Our AI service is temporarily unavailable. Please try again in a few minutes.'
const DIETARY_VALUES: DietaryInfo[] = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'nut-free', 'spicy']

function createQuotaError(
  errorType: APIQuotaError['errorType'],
  userMessage: string,
  retryAfter?: number
): APIQuotaError {
  const error = new Error(userMessage) as APIQuotaError
  error.isQuotaError = true
  error.errorType = errorType
  error.userMessage = userMessage
  error.retryAfter = retryAfter
  return error
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 11)
}

/* eslint-disable @typescript-eslint/no-explicit-any -- raw JSON from the API */
function toMenuItem(item: any, index: number): MenuItem {
  return {
    id: generateId(),
    name: typeof item?.name === 'string' && item.name.trim() ? item.name.trim() : 'New Item',
    description: typeof item?.description === 'string' && item.description.trim() ? item.description.trim() : undefined,
    price: typeof item?.price === 'string' ? item.price.replace(/[^\d.]/g, '') : undefined,
    dietary: Array.isArray(item?.dietary)
      ? item.dietary.filter((d: unknown): d is DietaryInfo => DIETARY_VALUES.includes(d as DietaryInfo))
      : [],
    order: index + 1,
    confidence: typeof item?.confidence === 'number' ? item.confidence : 0.8,
  }
}

function toMenu(data: any): Menu {
  if (!data || !Array.isArray(data.sections)) {
    throw new Error('We could not read a menu from this file. Please try a clearer photo or scan.')
  }

  const sections: MenuSection[] = data.sections.map((section: any, index: number) => ({
    id: generateId(),
    name: typeof section?.name === 'string' && section.name.trim() ? section.name.trim() : 'New Section',
    description: typeof section?.description === 'string' && section.description.trim() ? section.description.trim() : undefined,
    items: (Array.isArray(section?.items) ? section.items : []).map(toMenuItem),
    order: index + 1,
    column: 'column1' as const,
  }))
/* eslint-enable @typescript-eslint/no-explicit-any */

  const allItems = sections.flatMap((s) => s.items)
  const averageConfidence = allItems.length
    ? allItems.reduce((sum, item) => sum + (item.confidence ?? 0.8), 0) / allItems.length
    : 0

  return {
    id: generateId(),
    name: typeof data.name === 'string' && data.name.trim() ? data.name.trim() : 'Menu',
    sections,
    metadata: {
      originalFileName: '',
      processingTime: Date.now(),
      ocrAccuracy: averageConfidence,
      language: typeof data.language === 'string' ? data.language : 'en',
      imageQuality: 'high',
    },
    theme: getDefaultTheme(),
    currency: isKnownCurrencySymbol(data.currency) ? data.currency : '$',
    layout: '1col',
    createdAt: new Date(),
    lastModified: new Date(),
  }
}

function buildWarnings(menu: Menu): string[] {
  const warnings: string[] = []
  const items = menu.sections.flatMap((s) => s.items)

  if (menu.sections.length === 0) warnings.push('No menu sections were identified')
  if (items.length === 0) warnings.push('No menu items were extracted')

  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

  const withoutPrices = items.filter((item) => !item.price).length
  if (withoutPrices > 0) warnings.push(`${plural(withoutPrices, 'item has', 'items have')} no price`)

  const toReview = items.filter(needsReview).length
  if (toReview > 0) {
    warnings.push(`${plural(toReview, 'item is', 'items are')} marked "Please review" because Claude wasn't sure it read them correctly`)
  }

  return warnings
}

export class ClaudeService {
  async parseMenuImages(images: MenuImage[], signal?: AbortSignal): Promise<ClaudeParseResult> {
    if (images.length === 0) {
      throw new Error('No menu pages to process')
    }

    let response: Response
    try {
      response = await fetch('/api/parse-menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images }),
        signal,
      })
    } catch (error) {
      if (signal?.aborted) throw new Error('Processing was cancelled', { cause: error })
      console.error('Network error calling /api/parse-menu:', error)
      throw createQuotaError('service_unavailable', UNAVAILABLE_MESSAGE)
    }

    const body = await response.json().catch(() => ({}))

    if (!response.ok) {
      if (body.isQuotaError) {
        throw createQuotaError(body.errorType || 'service_unavailable', body.error || UNAVAILABLE_MESSAGE, body.retryAfter)
      }
      throw new Error(body.error || 'Failed to process menu')
    }

    const menu = toMenu(body.menu)
    return {
      menu,
      confidence: menu.metadata.ocrAccuracy,
      tokensUsed: body.tokensUsed || { input: 0, output: 0 },
      warnings: [...(body.warnings || []), ...buildWarnings(menu)],
    }
  }
}

function getDefaultTheme(): Menu['theme'] {
  return {
    name: 'default',
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      accent: '#f59e0b',
      background: '#ffffff',
      text: '#1f2937',
      sectionTitle: '#1f2937',
      menuItemTitle: '#1f2937',
      description: '#64748b',
      price: '#1f2937',
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      headingSize: '1.5rem',
      bodySize: '1rem',
      sectionTitleFont: 'Inter, system-ui, sans-serif',
      menuItemTitleFont: 'Inter, system-ui, sans-serif',
      descriptionFont: 'Inter, system-ui, sans-serif',
      priceFont: 'Inter, system-ui, sans-serif',
    },
    layout: {
      spacing: 'medium',
      border: 'rounded',
    },
  }
}

export const claudeService = new ClaudeService()
